export const EMPTY_STRUCTURED_REPORT = {
  summary: {},
  evidence: {},
  matching_metadata: {},
  module_rollup: [],
  covered_units: [],
  partial_units: [],
  missing_units: [],
  generation_summary: {},
  generated_tests: [],
};

export const COVERAGE_FILTERS = {
  REVIEW_NEEDED: 'review_needed',
  MISSING: 'missing',
  PARTIAL: 'partial',
  COVERED: 'covered',
  ALL: 'all',
};

export const COVERAGE_FILTER_OPTIONS = [
  { id: COVERAGE_FILTERS.REVIEW_NEEDED, label: 'Review needed' },
  { id: COVERAGE_FILTERS.MISSING, label: 'Missing' },
  { id: COVERAGE_FILTERS.PARTIAL, label: 'Partial match' },
  { id: COVERAGE_FILTERS.COVERED, label: 'Covered' },
  { id: COVERAGE_FILTERS.ALL, label: 'All' },
];

export const STATUS_LABELS = {
  covered: 'Covered',
  partial: 'Partial match',
  missing: 'Missing',
};

export const MATCH_STRENGTH_THRESHOLDS = {
  HIGH: 0.78,
  MEDIUM: 0.62,
};

export const normalizeStructuredReport = (structuredReport) => {
  const structured = structuredReport || EMPTY_STRUCTURED_REPORT;
  return {
    summary: structured.summary || {},
    evidence: structured.evidence || {},
    matchingMetadata: structured.matching_metadata || {},
    moduleRollup: structured.module_rollup || [],
    coveredUnits: structured.covered_units || [],
    partialUnits: structured.partial_units || structured.maybe_units || [],
    missingUnits: structured.missing_units || [],
    generatedTests: structured.generated_tests || [],
    generationSummary: structured.generation_summary || {},
  };
};

export const getValidationSections = (structuredReport) => normalizeStructuredReport(structuredReport);

export const getRecommendedActionPriority = (action) => {
  const normalized = (action || '').trim().toLowerCase();
  if (normalized === 'generate targeted test') return 1;
  if (normalized === 'review possible duplicate before generating') return 2;
  if (normalized === 'review matched test') return 3;
  if (normalized === 'no action needed') return 4;
  return 99;
};

export const getSourceSortKey = (unit = {}) => {
  const source = unit.source || {};
  return (source.pkb_entry_title || source.pkb_entry_key || unit.unit_name || unit.unit_text || '').toLowerCase();
};

export const sortCoverageUnitsForReview = (units = []) => (
  [...units].sort((left, right) => {
    const priorityDiff = getRecommendedActionPriority(left.recommended_action)
      - getRecommendedActionPriority(right.recommended_action);
    if (priorityDiff !== 0) return priorityDiff;
    return getSourceSortKey(left).localeCompare(getSourceSortKey(right));
  })
);

export const getReviewNeededUnits = (structuredReport) => {
  const { missingUnits, partialUnits } = normalizeStructuredReport(structuredReport);
  return sortCoverageUnitsForReview([...missingUnits, ...partialUnits]);
};

export const getAllCoverageUnits = (structuredReport) => {
  const { missingUnits, partialUnits, coveredUnits } = normalizeStructuredReport(structuredReport);
  return [
    ...sortCoverageUnitsForReview(missingUnits),
    ...sortCoverageUnitsForReview(partialUnits),
    ...coveredUnits,
  ];
};

export const filterCoverageUnits = (structuredReport, filterId) => {
  const { missingUnits, partialUnits, coveredUnits } = normalizeStructuredReport(structuredReport);
  switch (filterId) {
    case COVERAGE_FILTERS.REVIEW_NEEDED:
      return getReviewNeededUnits(structuredReport);
    case COVERAGE_FILTERS.MISSING:
      return sortCoverageUnitsForReview(missingUnits);
    case COVERAGE_FILTERS.PARTIAL:
      return sortCoverageUnitsForReview(partialUnits);
    case COVERAGE_FILTERS.COVERED:
      return coveredUnits;
    case COVERAGE_FILTERS.ALL:
      return getAllCoverageUnits(structuredReport);
    default:
      return getReviewNeededUnits(structuredReport);
  }
};

export const getCoverageFilterCounts = (structuredReport) => {
  const { missingUnits, partialUnits, coveredUnits } = normalizeStructuredReport(structuredReport);
  return {
    [COVERAGE_FILTERS.REVIEW_NEEDED]: missingUnits.length + partialUnits.length,
    [COVERAGE_FILTERS.MISSING]: missingUnits.length,
    [COVERAGE_FILTERS.PARTIAL]: partialUnits.length,
    [COVERAGE_FILTERS.COVERED]: coveredUnits.length,
    [COVERAGE_FILTERS.ALL]: missingUnits.length + partialUnits.length + coveredUnits.length,
  };
};

export const formatMatchScore = (value) => {
  if (value == null || value === '') return '-';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '-';
  return numeric.toFixed(2);
};

export const getMatchStrength = (score) => {
  const numeric = Number(score);
  if (score == null || score === '' || Number.isNaN(numeric)) {
    return { label: '-', level: 'unknown', raw: null };
  }
  if (numeric >= MATCH_STRENGTH_THRESHOLDS.HIGH) {
    return { label: 'High', level: 'high', raw: numeric };
  }
  if (numeric >= MATCH_STRENGTH_THRESHOLDS.MEDIUM) {
    return { label: 'Medium', level: 'medium', raw: numeric };
  }
  return { label: 'Low', level: 'low', raw: numeric };
};

export const formatSourceLabel = (source = {}) => {
  const title = source.pkb_entry_title || source.pkb_entry_key || source.pkb_entry_id;
  if (!title) return '-';
  if (source.pkb_entry_key && source.pkb_entry_title) {
    return `${source.pkb_entry_key}: ${source.pkb_entry_title}`;
  }
  return String(title);
};

export const getBestMatchedTestLabel = (unit = {}) => {
  const matchedTests = unit.matched_tests || [];
  if (!matchedTests.length) return null;
  const best = matchedTests[0];
  return best.test_title || best.test_id || null;
};

export const getRecommendedActionTone = (action) => {
  const normalized = (action || '').trim().toLowerCase();
  if (normalized === 'generate targeted test') return 'error';
  if (normalized === 'review possible duplicate before generating') return 'warning';
  if (normalized === 'review matched test') return 'info';
  if (normalized === 'no action needed') return 'success';
  return 'default';
};

export const getStatusChipColor = (status) => {
  if (status === 'covered') return 'success';
  if (status === 'partial') return 'warning';
  return 'default';
};

export const getEmptyFilterMessage = (filterId) => {
  switch (filterId) {
    case COVERAGE_FILTERS.MISSING:
      return 'No missing coverage units in this run.';
    case COVERAGE_FILTERS.PARTIAL:
      return 'No partial matches need review.';
    case COVERAGE_FILTERS.COVERED:
      return 'No covered units recorded for this run.';
    case COVERAGE_FILTERS.ALL:
      return 'No coverage units recorded for this run.';
    case COVERAGE_FILTERS.REVIEW_NEEDED:
    default:
      return 'Nothing needs review — all units are covered.';
  }
};

export const getCriticalQualifierSummary = (matchSignals = {}) => {
  const required = matchSignals.critical_terms_required || [];
  const matched = matchSignals.critical_terms_matched || [];
  const missing = matchSignals.critical_terms_missing || [];
  if (!required.length && !matched.length && !missing.length) {
    return null;
  }
  return { required, matched, missing };
};

export const GENERATION_NO_OP_REASONS = {
  NO_MISSING_UNITS: 'no_missing_units',
  ALL_DUPLICATE_SUPPRESSED: 'missing_units_all_duplicate_suppressed',
  ALL_BLOCKED: 'missing_units_all_blocked',
  CAP_REACHED: 'missing_units_generated_cap_reached',
  NO_ELIGIBLE: 'no_eligible_units',
};

export const GENERATION_NO_OP_MESSAGES = {
  [GENERATION_NO_OP_REASONS.NO_MISSING_UNITS]:
    'No fully missing units remain. Generate Missing Tests only targets fully missing units; partial matches require manual review.',
  [GENERATION_NO_OP_REASONS.ALL_DUPLICATE_SUPPRESSED]:
    'All missing units were skipped because matching suggestions or fingerprints already exist.',
  [GENERATION_NO_OP_REASONS.ALL_BLOCKED]:
    'All missing units were blocked as near-duplicates of existing tests.',
  [GENERATION_NO_OP_REASONS.CAP_REACHED]:
    'The generation cap was reached before any tests could be created in this batch.',
  [GENERATION_NO_OP_REASONS.NO_ELIGIBLE]:
    'No eligible missing units were found for generation (inactive units or module filter).',
};

export const formatGenerationNoOpReason = (reason, scope = {}) => {
  if (!reason) return null;
  const base = GENERATION_NO_OP_MESSAGES[reason] || `Generation completed with no new tests (${reason}).`;
  const partial = scope.partial_units_available;
  if (reason === GENERATION_NO_OP_REASONS.NO_MISSING_UNITS && partial > 0) {
    return `${base} ${partial} partial match unit(s) still need review.`;
  }
  return base;
};

export const getRunListCounts = (run = {}) => {
  const isValidation = run.run_type === 'validation';
  const covered = isValidation ? (run.covered_units ?? 0) : null;
  const partial = isValidation ? (run.partial_units ?? 0) : null;
  const missing = isValidation ? (run.missing_units ?? 0) : null;
  const reviewNeeded = isValidation ? (partial ?? 0) + (missing ?? 0) : null;
  return {
    covered,
    partial,
    missing,
    reviewNeeded,
    generated: run.generated_tests ?? 0,
  };
};

export const getGenerateMissingTestsState = ({
  latestValidationRunId,
  latestValidationSummary,
} = {}) => {
  if (!latestValidationRunId) {
    return {
      enabled: false,
      reason: 'Run a coverage validation first. Generate Missing Tests uses the latest completed validation run.',
      missing: 0,
      partial: 0,
      showReviewPartialHint: false,
    };
  }
  const missing = latestValidationSummary?.missing_units ?? 0;
  const partial = latestValidationSummary?.partial_units ?? 0;
  if (missing > 0) {
    return {
      enabled: true,
      reason: '',
      missing,
      partial,
      showReviewPartialHint: partial > 0,
    };
  }
  if (partial > 0) {
    return {
      enabled: false,
      reason: 'No fully missing units. Review partial matches to decide whether targeted tests are needed.',
      missing,
      partial,
      showReviewPartialHint: true,
    };
  }
  return {
    enabled: false,
    reason: 'No missing units in the latest validation — nothing to generate.',
    missing,
    partial,
    showReviewPartialHint: false,
  };
};
